import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { analyzeNameplate, mapToScreeningFields } from '../services/nameplateService';
import { triggerBenchStart, triggerBenchStop, getBenchTelemetry } from '../services/benchService';
import { cToF } from '../services/monitoringApi';
import {
  loadScreeningDraft, saveScreeningDraft, clearScreeningDraft, screeningHasContent, captureStillLive,
} from '../services/screeningDraft';

// ── Theme ──────────────────────────────────────────────────────────────────

const ThemeCtx = createContext({ isDark: false, toggle: () => {} });

function useScreenTheme() {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('byrdsense-screening-theme') === 'dark'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('byrdsense-screening-theme', isDark ? 'dark' : 'light'); } catch {}
  }, [isDark]);
  return { isDark, toggle: () => setIsDark(d => !d) };
}

function c(isDark) {
  const TEAL = '#4A90E2';
  if (isDark) return {
    bg: '#0A1B3B', card: '#0A1B3B', border: 'rgba(120,183,255,0.28)',
    text: '#FFFFFF', textSec: '#5E6F8E', textMuted: '#5E6F8E',
    inputBg: '#0A1B3B', inputText: '#fff', inputPh: 'gray-600',
    logoFilter: 'brightness(0) invert(1)',
    teal: TEAL, errBg: '#FDE7E1', errBorder: '#C2410C', errText: '#F0563C',
    backBtnBg: '#0A1B3B', backBtnText: '#5E6F8E',
  };
  return {
    bg: '#FFFFFF', card: '#F6F4EE', border: 'rgba(10,27,59,0.12)',
    text: '#0A1B3B', textSec: '#3A4D6E', textMuted: '#5E6F8E',
    inputBg: '#F6F4EE', inputText: '#0A1B3B', inputPh: 'gray-400',
    logoFilter: 'none',
    teal: TEAL, errBg: '#FEF2F2', errBorder: '#FECACA', errText: '#C2410C',
    backBtnBg: '#F6F4EE', backBtnText: '#3A4D6E',
  };
}

function SunIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
}
function MoonIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
}
function CameraIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>;
}
function ImageIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
}

// ── Constants ──────────────────────────────────────────────────────────────

const TEAL = '#4A90E2';
const ACCESS_CODE = 'ByrdScreen2026';
const SCREENING_API = 'https://slrai28hb2.execute-api.us-east-1.amazonaws.com/prod/reports/screening';
const UPLOAD_API = 'https://slrai28hb2.execute-api.us-east-1.amazonaws.com/prod/reports/upload-url';
const ANALYZE_API = 'https://slrai28hb2.execute-api.us-east-1.amazonaws.com/prod/reports/analyze-thermal';
const ANALYZE_VIB_API = 'https://slrai28hb2.execute-api.us-east-1.amazonaws.com/prod/reports/analyze-vibration';
const EXPLAIN_VIB_API = 'https://slrai28hb2.execute-api.us-east-1.amazonaws.com/prod/reports/explain-vibration';
const MIN_READINGS = 2; // small hedge against a single fluke packet; confirm real per-minute report rate at the shop dress rehearsal
const MAX_PHOTOS = 3;
const EMPTY_MOTOR = { name: '', location: '', hp: '', rpm: '', vibration: '', temperature: '', notes: '', frame: '', voltage: '', manufacturer: '', serialNumber: '' };
const newFacility = () => ({ company: '', location: '', contact: '', date: new Date().toISOString().slice(0, 10), screenedBy: 'Byrdsense' });

// ── ISO 20816 (formerly ISO 10816) ─────────────────────────────────────────

const ISO = {
  small:  { good: 0.71, satisfactory: 1.8,  unsatisfactory: 4.5 },
  medium: { good: 1.12, satisfactory: 2.8,  unsatisfactory: 7.1 },
  large:  { good: 1.8,  satisfactory: 4.5,  unsatisfactory: 11.2 },
};
function sizeClass(hp) { if (!hp) return 'small'; if (hp > 100) return 'large'; if (hp > 15) return 'medium'; return 'small'; }
function calcGrade(vibration, hp) {
  if (vibration == null || vibration === '') return null;
  const v = Number(vibration); if (isNaN(v)) return null;
  const t = ISO[sizeClass(Number(hp) || 0)];
  if (v <= t.good) return 'A'; if (v <= t.satisfactory) return 'B';
  if (v <= t.unsatisfactory) return 'C'; if (v <= t.unsatisfactory * 1.58) return 'D'; return 'F';
}
const GRADE_COLORS = { A: '#1E7A47', B: '#1E7A47', C: '#9A5B00', D: '#C2410C', F: '#C2410C' };
const GRADE_LABELS = { A: 'Good', B: 'Good', C: 'Watch', D: 'Attention', F: 'Critical' };
function gradeStatus(g) { return GRADE_LABELS[g] || 'Unknown'; }
// Grade text = reading + range + action. Never a cause, never a prediction
// (CLAUDE.md "Honest claims", "Grade actions"); it prints under the badge in
// the screening PDF, so it must match the PDF's own grade actions.
const GRADE_ACTION = {
  A: 'No additional action indicated.',
  B: 'No additional action indicated.',
  C: 'Re-check within 30 days.',
  D: 'Plan an inspection within two weeks.',
  F: 'Inspect within 48 hours.',
};
export function gradeFinding(name, grade, vib, temp) {
  if (!GRADE_ACTION[grade]) return '';
  const v = Number(vib), t = Number(temp);
  const reading = `${v} mm/s${(grade === 'D' || grade === 'F') && t ? `, ${t}°F` : ''}`;
  return `${name}: ${reading}, in the ${grade} (${GRADE_LABELS[grade]}) range. ${GRADE_ACTION[grade]}`;
}
function gradeRec(name, grade, vib, temp) { return (grade === 'A' || grade === 'B') ? '' : gradeFinding(name, grade, vib, temp); }

// ── Shared UI ──────────────────────────────────────────────────────────────

function GradeBadge({ grade, size = 'md' }) {
  if (!grade) return null;
  const s = size === 'lg' ? 40 : 28, fs = size === 'lg' ? 20 : 14;
  return <div style={{ width: s, height: s, borderRadius: '50%', backgroundColor: GRADE_COLORS[grade] || '#666', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: fs, color: '#fff', flexShrink: 0 }}>{grade}</div>;
}

function Badge({ color, label }) {
  return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: `${color}15`, color }}>
    <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, display: 'inline-block' }} />{label}
  </span>;
}

function Shell({ children }) {
  const { isDark, toggle } = useContext(ThemeCtx);
  const k = c(isDark);
  return (
    <div style={{ backgroundColor: k.bg, color: k.text, transition: 'background-color 0.2s, color 0.2s' }} className="min-h-screen">
      <style>{`@keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      <div style={{ backgroundColor: k.bg, borderBottom: `1px solid ${k.border}`, transition: 'background-color 0.2s' }} className="sticky top-0 z-50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link to="/"><img src={isDark ? '/assets/brand/byrdsense-icon-reversed.svg' : '/assets/brand/byrdsense-icon-color.svg'} alt="Byrdsense" style={{ width: 36, height: 'auto' }} /></Link>
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: k.textMuted }}>Screening Tool</span>
            <button onClick={toggle} title={isDark ? 'Light mode' : 'Dark mode'}
              className="flex items-center justify-center w-9 h-9 rounded-lg transition-colors"
              style={{ color: k.textMuted }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              {isDark ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-lg mx-auto px-4 py-6">{children}</div>
    </div>
  );
}

function StepHeader({ num, total, title, subtitle }) {
  const { isDark } = useContext(ThemeCtx); const k = c(isDark);
  return <div className="mb-6">
    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: TEAL }}>Step {num} of {total || 4}</div>
    <h1 className="text-2xl font-black">{title}</h1>
    {subtitle && <p className="text-sm mt-1" style={{ color: k.textSec }}>{subtitle}</p>}
  </div>;
}

function Field({ label, required, children }) {
  const { isDark } = useContext(ThemeCtx); const k = c(isDark);
  return <div><label className="block text-sm font-semibold mb-1.5" style={{ color: k.textSec }}>{label}{required && <span style={{ color: TEAL }}> *</span>}</label>{children}</div>;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// ── Live Sensor Panel ──────────────────────────────────────────────────────

function SensorPanel({ telemetry, k }) {
  return (
    <div className="space-y-3 mb-4">
      {['BENCH-DE', 'BENCH-NDE'].map(id => {
        const sensor = telemetry[id];
        const latest = sensor?.latest;
        const count = sensor?.count || 0;
        const lastTs = latest?.timestamp ? new Date(latest.timestamp).getTime() : 0;
        const ageSec = lastTs ? Math.floor((Date.now() - lastTs) / 1000) : Infinity;
        const sensorReady = count >= MIN_READINGS;

        let statusColor, statusBg, statusLabel;
        if (count > 0 && ageSec < 30) {
          statusColor = '#1E7A47'; statusBg = '#E8F6EE'; statusLabel = 'Live';
        } else if (count > 0 && ageSec < 120) {
          statusColor = '#92400e'; statusBg = '#fef3c7'; statusLabel = 'Stale';
        } else if (count > 0) {
          statusColor = '#5E6F8E'; statusBg = '#f3f4f6'; statusLabel = `${count} captured`;
        } else {
          statusColor = '#b91c1c'; statusBg = '#fee2e2'; statusLabel = 'Waiting...';
        }

        const rmsVel = latest?.overallRmsVelMmSec;
        const rpm = latest?.rpm;
        const tempC = latest?.temperatureC;

        return (
          <div key={id} className="p-3.5 rounded-xl" style={{ backgroundColor: k.card, border: `1px solid ${k.border}` }}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-bold">{id === 'BENCH-DE' ? 'Drive End (DE)' : 'Non-Drive End (NDE)'}</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: statusBg, color: statusColor }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, display: 'inline-block', animation: ageSec < 30 ? 'pulse-dot 1.5s ease-in-out infinite' : 'none' }} />
                {statusLabel}
              </span>
            </div>
            {/* Progress bar */}
            <div className="mb-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-semibold" style={{ color: sensorReady ? '#1E7A47' : k.textMuted }}>
                  {count} / {MIN_READINGS} readings{sensorReady ? ' — Ready' : ''}
                </span>
              </div>
              <div className="rounded-full overflow-hidden" style={{ height: 5, backgroundColor: k.border }}>
                <div className="rounded-full" style={{ height: '100%', width: `${Math.min(100, (count / MIN_READINGS) * 100)}%`, backgroundColor: sensorReady ? '#16a34a' : TEAL, transition: 'width 0.5s ease-out' }} />
              </div>
            </div>
            {count > 0 ? (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-xs" style={{ color: k.textMuted }}>RMS Vel</div>
                  <div className="text-base font-extrabold" style={{ color: TEAL }}>{rmsVel != null ? Number(rmsVel).toFixed(1) : '—'}</div>
                  <div className="text-xs" style={{ color: k.textMuted }}>mm/s</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: k.textMuted }}>RPM</div>
                  <div className="text-base font-extrabold" style={{ color: TEAL }}>{rpm != null ? Math.round(Number(rpm)) : '—'}</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: k.textMuted }}>Temp</div>
                  <div className="text-base font-extrabold" style={{ color: TEAL }}>{tempC != null ? cToF(tempC) : '—'}</div>
                  <div className="text-xs" style={{ color: k.textMuted }}>&deg;F</div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-center py-2" style={{ color: k.textMuted }}>Waiting for sensor data...</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function ScreeningApp() {
  const theme = useScreenTheme();
  // A screening in progress comes back after a reload / closed tab (rule 8;
  // screeningDraft.js). The access code is never stored — it is asked again,
  // then everything else is restored.
  const [draft] = useState(() => loadScreeningDraft());
  const [restored, setRestored] = useState(Boolean(draft));
  const restoredAt = draft && draft.savedAt ? draft.savedAt : null;
  const [accessGranted, setAccessGranted] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [step, setStep] = useState(draft ? draft.step : 1);
  const [facility, setFacility] = useState(draft ? { ...newFacility(), ...draft.facility } : newFacility);
  const [motors, setMotors] = useState(draft ? draft.motors : []);
  const [currentMotor, setCurrentMotor] = useState(draft && draft.currentMotor ? { ...EMPTY_MOTOR, ...draft.currentMotor } : EMPTY_MOTOR);
  const [editingIdx, setEditingIdx] = useState(draft && Number.isInteger(draft.editingIdx) ? draft.editingIdx : null);
  const [generating, setGenerating] = useState(false);
  const [reportUrl, setReportUrl] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [sendEmail, setSendEmail] = useState(draft && draft.sendEmail ? draft.sendEmail : '');
  const [sendStatus, setSendStatus] = useState('idle');
  const nameRef = useRef(null);
  // Motors whose plain-English explanation is being fetched right now. After a
  // reload (or a failed call) nothing is in flight, so the panel offers
  // "Generate" instead of spinning forever.
  const [explaining, setExplaining] = useState(() => new Set());

  // ── Photo attachment state ──────────────────────────────────────────
  // [{file, preview, uploading, s3Key}] — after a reload only uploaded ones (s3Key) come back
  const [currentPhotos, setCurrentPhotos] = useState(
    () => ((draft && draft.currentPhotoKeys) || []).map(s3Key => ({ file: null, preview: null, uploading: false, s3Key })),
  );
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);
  const [thermalAnalysis, setThermalAnalysis] = useState(draft && draft.thermalAnalysis ? draft.thermalAnalysis : '');
  const [analyzingThermal, setAnalyzingThermal] = useState(false);
  const [vibrationAnalysis, setVibrationAnalysis] = useState(draft && draft.vibrationAnalysis ? draft.vibrationAnalysis : '');
  const [analyzingVibration, setAnalyzingVibration] = useState(false);

  // ── Live capture state ──────────────────────────────────────────────
  // A capture running at the reload resumes polling (no new START) while its
  // 30-min registration is alive; otherwise the tech is told to start again.
  const resumable = captureStillLive(draft && draft.capture);
  const [capturing, setCapturing] = useState(resumable);
  const [captureId, setCaptureId] = useState(resumable ? draft.capture.captureId : null);
  const [captureStart, setCaptureStart] = useState(resumable ? draft.capture.captureStart : null);
  const [captureNote, setCaptureNote] = useState(
    draft && draft.capture ? (resumable ? 'resumed' : 'expired') : null,
  );
  const [telemetry, setTelemetry] = useState({});
  const pollRef = useRef(null);

  // ── Nameplate OCR state ─────────────────────────────────────────────
  const [scanning, setScanning] = useState(false);
  const [ocrNotice, setOcrNotice] = useState('');
  const fileInputRef = useRef(null);

  const k = c(theme.isDark);
  const inputStyle = { backgroundColor: k.inputBg, border: `1px solid ${k.border}`, minHeight: 48, color: k.inputText, transition: 'background-color 0.2s, border-color 0.2s, color 0.2s' };
  const inputCls = "w-full px-4 py-3.5 rounded-xl text-base outline-none transition";
  const btnCls = "w-full py-4 rounded-xl text-base font-bold text-white transition-all disabled:opacity-40";
  const btnStyle = { backgroundColor: TEAL, minHeight: 52 };
  const focusTeal = e => { e.target.style.borderColor = TEAL; };
  const blurBorder = e => { e.target.style.borderColor = k.border; };

  // ── Telemetry polling ───────────────────────────────────────────────

  useEffect(() => {
    if (capturing && captureStart && accessGranted) {
      const poll = async () => {
        try {
          // captureId scopes the query to this screening capture's readings
          // (motorId=screening_<captureId>), keyed by BENCH-DE / BENCH-NDE.
          const result = await getBenchTelemetry(captureStart, captureId);
          if (result.success) setTelemetry(result.data.sensors || {});
        } catch (err) {
          console.error('Telemetry poll error:', err);
        }
      };
      poll();
      pollRef.current = setInterval(poll, 3000);
      return () => clearInterval(pollRef.current);
    } else {
      clearInterval(pollRef.current);
    }
  }, [capturing, captureStart, captureId, accessGranted]);

  // ── Draft autosave ──────────────────────────────────────────────────
  useEffect(() => {
    if (!screeningHasContent({ facility, motors, currentMotor })) return;
    saveScreeningDraft({
      step: Math.min(step, 3), facility, motors, currentMotor, editingIdx, sendEmail,
      currentPhotoKeys: currentPhotos.filter(p => p.s3Key).map(p => p.s3Key),
      thermalAnalysis, vibrationAnalysis,
      capture: capturing && captureId ? { captureId, captureStart } : null,
    });
  }, [step, facility, motors, currentMotor, editingIdx, sendEmail, currentPhotos, thermalAnalysis, vibrationAnalysis, capturing, captureId, captureStart]);

  // Derived: can we save this motor?
  const deCount = telemetry['BENCH-DE']?.count || 0;
  const ndeCount = telemetry['BENCH-NDE']?.count || 0;
  const bothReady = deCount >= MIN_READINGS && ndeCount >= MIN_READINGS;

  // Best vibration from DE sensor (primary bearing position)
  const liveVibDE = telemetry['BENCH-DE']?.latest?.overallRmsVelMmSec;
  const liveVibNDE = telemetry['BENCH-NDE']?.latest?.overallRmsVelMmSec;
  const liveTempDE = telemetry['BENCH-DE']?.latest?.temperatureC;
  const liveRPM = telemetry['BENCH-DE']?.latest?.rpm || telemetry['BENCH-NDE']?.latest?.rpm;
  // Use the higher vibration between DE and NDE as the motor's overall reading
  const liveVib = (liveVibDE != null && liveVibNDE != null) ? Math.max(Number(liveVibDE), Number(liveVibNDE)) : (liveVibDE ?? liveVibNDE);

  // ── Access code gate ───────────────────────────────────────────────

  if (!accessGranted) {
    return (
      <ThemeCtx.Provider value={theme}>
        <div style={{ backgroundColor: k.bg, transition: 'background-color 0.2s' }} className="min-h-screen flex items-center justify-center p-4">
          <div className="w-full max-w-sm">
            <div className="text-center mb-8">
              <img src={theme.isDark ? '/assets/brand/byrdsense-wordmark-reversed.svg' : '/assets/brand/byrdsense-wordmark-color.svg'} alt="Byrdsense" style={{ width: 160, height: 'auto', margin: '0 auto' }} />
              <p className="text-sm mt-3" style={{ color: k.textMuted }}>Field Screening Tool</p>
            </div>
            <form onSubmit={e => { e.preventDefault(); if (codeInput === ACCESS_CODE) { setAccessGranted(true); setCodeError(false); } else setCodeError(true); }}>
              <input type="password" value={codeInput}
                onChange={e => { setCodeInput(e.target.value); setCodeError(false); }}
                placeholder="Enter access code" autoFocus className={inputCls}
                style={{ ...inputStyle, borderColor: codeError ? '#C2410C' : k.border }}
                onFocus={focusTeal} onBlur={e => e.target.style.borderColor = codeError ? '#C2410C' : k.border} />
              {codeError && <p className="text-sm mt-2" style={{ color: '#C2410C' }}>Invalid access code</p>}
              <button type="submit" className={btnCls} style={{ ...btnStyle, marginTop: 16 }}>Enter</button>
            </form>
          </div>
        </div>
      </ThemeCtx.Provider>
    );
  }

  // ── Nameplate OCR handler ───────────────────────────────────────────

  const handleNameplateFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setOcrNotice('');
    try {
      const result = await analyzeNameplate(file);
      if (result.success && result.data) {
        const mapped = mapToScreeningFields(result.data);
        setCurrentMotor(m => ({
          ...m,
          hp: mapped.hp || m.hp,
          rpm: mapped.rpm || m.rpm,
          frame: mapped.frame || m.frame,
          voltage: mapped.voltage || m.voltage,
          manufacturer: mapped.manufacturer || m.manufacturer,
          serialNumber: mapped.serialNumber || m.serialNumber,
        }));
        const conf = mapped.confidence;
        setOcrNotice(`Nameplate scanned (${conf} confidence) — review fields below`);
      } else {
        setOcrNotice('Could not read nameplate — enter details manually');
      }
    } catch {
      setOcrNotice('Nameplate scan failed — enter details manually');
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Photo upload handler ────────────────────────────────────────────

  const handlePhotoFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_PHOTOS - currentPhotos.length;
    const toAdd = files.slice(0, remaining);
    if (!toAdd.length) return;

    // Generate a screening ID for S3 path
    const screeningId = `screening_${Date.now()}`;
    const motorIdx = editingIdx !== null ? editingIdx : motors.length;

    setPhotoUploading(true);
    const newPhotos = [];

    for (const file of toAdd) {
      const preview = URL.createObjectURL(file);
      const photoEntry = { file, preview, uploading: true, s3Key: null };
      newPhotos.push(photoEntry);
    }
    setCurrentPhotos(prev => [...prev, ...newPhotos]);

    // Upload each photo to S3 via pre-signed URL
    for (let i = 0; i < toAdd.length; i++) {
      const file = toAdd[i];
      const photoIdx = currentPhotos.length + i;
      try {
        // Get pre-signed PUT URL
        const res = await fetch(UPLOAD_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            screeningId,
            motorIndex: motorIdx,
            photoIndex: photoIdx,
            contentType: file.type || 'image/jpeg',
          }),
        });
        const data = await res.json();
        if (data.uploadUrl && data.s3Key) {
          // Upload file directly to S3
          await fetch(data.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type || 'image/jpeg' },
            body: file,
          });
          // Update entry with s3Key
          setCurrentPhotos(prev => prev.map((p, j) => j === photoIdx ? { ...p, uploading: false, s3Key: data.s3Key } : p));
        } else {
          setCurrentPhotos(prev => prev.map((p, j) => j === photoIdx ? { ...p, uploading: false } : p));
        }
      } catch (err) {
        console.error('Photo upload error:', err);
        setCurrentPhotos(prev => prev.map((p, j) => j === photoIdx ? { ...p, uploading: false } : p));
      }
    }
    setPhotoUploading(false);
    if (photoInputRef.current) photoInputRef.current.value = '';

    // Trigger AI thermal analysis on the first photo (if no analysis yet)
    if (!thermalAnalysis) {
      // Find the first uploaded s3Key from all current photos
      setAnalyzingThermal(true);
      // Wait a tick for state to settle, then read from latest photos
      setTimeout(async () => {
        try {
          // Get the first s3Key we have
          const allPhotos = [...currentPhotos, ...newPhotos];
          const firstKey = allPhotos.find(p => p.s3Key)?.s3Key;
          if (!firstKey) { setAnalyzingThermal(false); return; }
          const res = await fetch(ANALYZE_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ s3Key: firstKey }),
          });
          const data = await res.json();
          if (data.analysis) setThermalAnalysis(data.analysis);
        } catch (err) {
          console.error('Thermal analysis error:', err);
        } finally {
          setAnalyzingThermal(false);
        }
      }, 500);
    }
  };

  const removePhoto = (idx) => {
    setCurrentPhotos(prev => {
      const removed = prev[idx];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // ── Vibration analysis helper ───────────────────────────────────────

  const fetchVibrationAnalysis = async (motor, motorIdx) => {
    try {
      const res = await fetch(ANALYZE_VIB_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: motor.name,
          hp: motor.hp,
          rpm: motor.rpm,
          vibration: motor.vibration,
          temperature: motor.temperature,
          grade: motor.grade,
        }),
      });
      const data = await res.json();
      if (data.analysis) {
        setMotors(prev => prev.map((m, i) => i === motorIdx ? { ...m, vibrationAnalysis: data.analysis } : m));
      }
    } catch (err) {
      console.error('Vibration analysis error:', err);
    }
  };

  // ── Quick explanation helper (plain-English, tech-only) ────────────

  const fetchQuickExplanation = async (motor, motorIdx) => {
    setExplaining(prev => new Set(prev).add(motorIdx));
    try {
      const res = await fetch(EXPLAIN_VIB_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: motor.name,
          hp: motor.hp,
          rpm: motor.rpm,
          vibration: motor.vibration,
          temperature: motor.temperature,
        }),
      });
      const data = await res.json();
      if (data.explanation) {
        setMotors(prev => prev.map((m, i) => i === motorIdx ? { ...m, quickExplanation: data.explanation } : m));
      }
    } catch (err) {
      console.error('Quick explanation error:', err);
    } finally {
      setExplaining(prev => { const next = new Set(prev); next.delete(motorIdx); return next; });
    }
  };

  // ── Start capture ───────────────────────────────────────────────────

  const handleStartCapture = async () => {
    setCaptureNote(null);
    const id = `screening_${Date.now()}`;
    setCaptureId(id);
    const now = new Date().toISOString();
    setCaptureStart(now);
    setTelemetry({});
    setCapturing(true);
    await triggerBenchStart(id, { facilityName: facility.company, hp: currentMotor.hp, rpm: currentMotor.rpm });
  };

  // ── Stop capture & save motor ───────────────────────────────────────

  const handleStopAndSave = async () => {
    // Stop sensors
    if (captureId) await triggerBenchStop(captureId);
    clearInterval(pollRef.current);

    // Build motor from live data
    const vibMmS = liveVib != null ? Number(Number(liveVib).toFixed(2)) : Number(currentMotor.vibration) || 0;
    const tempF = liveTempDE != null ? cToF(liveTempDE) : (Number(currentMotor.temperature) || 0);
    const motorRpm = liveRPM && Number(liveRPM) > 0 ? Math.round(Number(liveRPM)) : (Number(currentMotor.rpm) || 0);
    const hp = Number(currentMotor.hp) || 0;

    const grade = calcGrade(vibMmS, hp);
    const photoKeys = currentPhotos.filter(p => p.s3Key).map(p => p.s3Key);
    const m = {
      ...currentMotor,
      hp,
      rpm: motorRpm,
      vibration: vibMmS,
      temperature: tempF,
      grade: grade || 'C',
      status: gradeStatus(grade),
      finding: gradeFinding(currentMotor.name, grade, vibMmS, tempF),
      recommendation: gradeRec(currentMotor.name, grade, vibMmS, tempF),
      photos: photoKeys,
      thermalAnalysis: thermalAnalysis,
    };

    let savedIdx;
    if (editingIdx !== null) {
      setMotors(prev => prev.map((old, i) => i === editingIdx ? m : old));
      savedIdx = editingIdx;
      setEditingIdx(null);
    } else {
      savedIdx = motors.length;
      setMotors(prev => [...prev, m]);
    }

    // Fire-and-forget vibration analysis + quick explanation
    fetchVibrationAnalysis(m, savedIdx);
    fetchQuickExplanation(m, savedIdx);

    // Reset for next motor
    setCapturing(false);
    setCaptureId(null);
    setCaptureStart(null);
    setCaptureNote(null);
    setTelemetry({});
    setOcrNotice('');
    setCurrentPhotos([]);
    setThermalAnalysis('');
    setVibrationAnalysis('');
    setCurrentMotor(EMPTY_MOTOR);
    setTimeout(() => nameRef.current?.focus(), 100);
  };

  // ── Manual add (fallback — no sensors) ──────────────────────────────

  const addOrUpdateMotor = () => {
    const grade = calcGrade(currentMotor.vibration, currentMotor.hp);
    const photoKeys = currentPhotos.filter(p => p.s3Key).map(p => p.s3Key);
    const m = { ...currentMotor, hp: Number(currentMotor.hp) || 0, rpm: Number(currentMotor.rpm) || 0, vibration: Number(currentMotor.vibration) || 0, temperature: Number(currentMotor.temperature) || 0, grade: grade || 'C', status: gradeStatus(grade), finding: gradeFinding(currentMotor.name, grade, currentMotor.vibration, currentMotor.temperature), recommendation: gradeRec(currentMotor.name, grade, currentMotor.vibration, currentMotor.temperature), photos: photoKeys, thermalAnalysis: thermalAnalysis };
    let savedIdx;
    if (editingIdx !== null) { setMotors(prev => prev.map((old, i) => i === editingIdx ? m : old)); savedIdx = editingIdx; setEditingIdx(null); }
    else { savedIdx = motors.length; setMotors(prev => [...prev, m]); }
    fetchVibrationAnalysis(m, savedIdx);
    fetchQuickExplanation(m, savedIdx);
    setCurrentMotor({ name: '', location: '', hp: '', rpm: '', vibration: '', temperature: '', notes: '', frame: '', voltage: '', manufacturer: '', serialNumber: '' });
    setCurrentPhotos([]);
    setThermalAnalysis(''); setVibrationAnalysis('');
    setOcrNotice('');
    setTimeout(() => nameRef.current?.focus(), 100);
  };

  const canAddMotor = currentMotor.name && currentMotor.vibration;
  const canStartCapture = currentMotor.name && !capturing;

  const editMotor = (m, i, goToStep2) => {
    setCurrentMotor({ ...m, hp: String(m.hp), rpm: String(m.rpm), vibration: String(m.vibration), temperature: String(m.temperature), frame: m.frame || '', voltage: m.voltage || '', manufacturer: m.manufacturer || '', serialNumber: m.serialNumber || '' });
    setCurrentPhotos((m.photos || []).map(s3Key => ({ file: null, preview: null, uploading: false, s3Key })));
    setThermalAnalysis(m.thermalAnalysis || '');
    setVibrationAnalysis(m.vibrationAnalysis || '');
    setEditingIdx(i);
    if (goToStep2) setStep(2);
  };

  // ── Handlers ───────────────────────────────────────────────────────

  async function handleGenerate() {
    setStep(4); setGenerating(true); setReportError(null);
    const payload = { facility: { company: facility.company, location: facility.location, contact: facility.contact, date: formatDate(facility.date), screenedBy: facility.screenedBy }, motors: motors.map(m => ({ name: m.name, location: m.location, hp: m.hp, rpm: m.rpm, grade: m.grade, vibration: m.vibration, temperature: m.temperature, status: m.status, finding: m.finding, recommendation: m.recommendation, photos: m.photos || [], thermalAnalysis: m.thermalAnalysis || '', vibrationAnalysis: m.vibrationAnalysis || '' })) };
    try {
      const res = await fetch(SCREENING_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.downloadUrl) setReportUrl(data.downloadUrl);
      else setReportError(data.error || 'Failed to generate report');
    } catch { setReportError('Network error — check your internet connection and try again.'); }
    finally { setGenerating(false); }
  }

  function handleSendEmail() {
    setSendStatus('sending');
    window.location.href = `mailto:${sendEmail}?subject=Equipment Health Screening Report - ${facility.company}&body=Your equipment health screening report is ready.%0A%0ADownload your report here:%0A${encodeURIComponent(reportUrl)}%0A%0AThis link is valid for 1 hour.%0A%0AByrdsense%0Ainfo@byrdsense.com%0A(423) 790-4745`;
    setTimeout(() => setSendStatus('sent'), 1000);
  }

  function handleReset() {
    // Clean up any active capture
    if (capturing && captureId) triggerBenchStop(captureId);
    clearInterval(pollRef.current);
    clearScreeningDraft(); setRestored(false); setCaptureNote(null);
    setStep(1); setFacility(newFacility());
    setMotors([]); setCurrentMotor(EMPTY_MOTOR);
    setEditingIdx(null); setReportUrl(null); setReportError(null); setSendEmail(''); setSendStatus('idle');
    setCapturing(false); setCaptureId(null); setCaptureStart(null); setTelemetry({}); setOcrNotice('');
    setCurrentPhotos([]); setThermalAnalysis(''); setVibrationAnalysis('');
  }

  // ── Render helpers ──────────────────────────────────────────────────

  const liveGrade = capturing
    ? (liveVib != null ? calcGrade(liveVib, currentMotor.hp) : null)
    : calcGrade(currentMotor.vibration, currentMotor.hp);

  const motorCard = (m, i, actions) => (
    <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: k.card, border: `1px solid ${k.border}`, transition: 'background-color 0.2s' }}>
      <GradeBadge grade={m.grade} size={actions === 'review' ? 'lg' : 'md'} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold truncate">{m.name}</div>
        <div className="text-xs" style={{ color: k.textMuted }}>{m.location} · {m.hp} HP{actions === 'review' ? ` · ${m.rpm} RPM` : ` · ${m.vibration} mm/s`}</div>
        {actions === 'review' && <div className="text-xs mt-1" style={{ color: GRADE_COLORS[m.grade] }}>{m.vibration} mm/s · {m.temperature}°F · {m.status}</div>}
      </div>
      {!capturing && <button onClick={() => editMotor(m, i, actions === 'review')} className="text-xs font-semibold px-2 py-1 rounded" style={{ color: TEAL }}>Edit</button>}
      {!capturing && <button onClick={() => setMotors(prev => prev.filter((_, j) => j !== i))} className="text-xs font-semibold px-2 py-1 rounded" style={{ color: '#C2410C' }}>Delete</button>}
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <ThemeCtx.Provider value={theme}>
      <Shell>
        {/* Hidden file inputs */}
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleNameplateFile} style={{ display: 'none' }} />
        <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handlePhotoFiles} style={{ display: 'none' }} />

        {/* Restored after a reload (screeningDraft.js) */}
        {restored && (
          <div className="p-3 rounded-xl mb-4 text-sm" style={{ backgroundColor: `${TEAL}12`, border: `1px solid ${TEAL}40`, color: k.text }}>
            <div className="font-semibold">
              Restored the screening in progress{facility.company ? ` for ${facility.company}` : ''} — {motors.length} asset{motors.length === 1 ? '' : 's'} captured.
            </div>
            <div className="text-xs mt-1" style={{ color: k.textMuted }}>
              Saved on this device{restoredAt ? ` ${new Date(restoredAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}.{' '}
              <button
                onClick={() => { if (window.confirm('Start over? The screening in progress will be removed from this device.')) handleReset(); }}
                className="font-semibold underline" style={{ color: k.textSec }}>
                Start over
              </button>
            </div>
          </div>
        )}
        {captureNote === 'resumed' && capturing && (
          <div className="p-3 rounded-xl mb-4 text-sm font-semibold" style={{ backgroundColor: '#E8F6EE', border: '1px solid #3DD68C', color: '#1E7A47' }}>
            Capture resumed after the reload — readings since {new Date(captureStart).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} are kept.
          </div>
        )}
        {captureNote === 'expired' && !capturing && (
          <div className="p-3 rounded-xl mb-4 text-sm font-semibold" style={{ backgroundColor: k.errBg, border: `1px solid ${k.errBorder}`, color: k.errText }}>
            The capture from before the reload has expired (30 min). Tap Start Capture again for this motor.
          </div>
        )}

        {/* Step 1 — Job Setup */}
        {step === 1 && <>
          <StepHeader num={1} title="Job Setup" subtitle="Enter facility information" />
          <div className="space-y-4">
            <Field label="Facility Name" required><input value={facility.company} onChange={e => setFacility(f => ({ ...f, company: e.target.value }))} placeholder="e.g. Sample Manufacturing Co." autoFocus className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>
            <Field label="Facility Location" required><input value={facility.location} onChange={e => setFacility(f => ({ ...f, location: e.target.value }))} placeholder="City, State" className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>
            <Field label="Contact Name" required><input value={facility.contact} onChange={e => setFacility(f => ({ ...f, contact: e.target.value }))} placeholder="e.g. John Smith, Plant Manager" className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>
            <Field label="Date"><input type="date" value={facility.date} onChange={e => setFacility(f => ({ ...f, date: e.target.value }))} className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>
            <Field label="Technician"><input value={facility.screenedBy} onChange={e => setFacility(f => ({ ...f, screenedBy: e.target.value }))} className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>
          </div>
          <button disabled={!facility.company || !facility.location || !facility.contact} onClick={() => setStep(2)} className={btnCls} style={{ ...btnStyle, marginTop: 24 }}>Start Screening</button>
        </>}

        {/* Step 2 — Motor Capture */}
        {step === 2 && <>
          <StepHeader num={2} title="Asset Capture" subtitle={`Asset ${motors.length + 1}${motors.length > 0 ? ` · ${motors.length} captured` : ''}`} />

          {/* Live grade badge when we have data */}
          {liveGrade && (
            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl" style={{ backgroundColor: `${GRADE_COLORS[liveGrade]}15`, border: `1px solid ${GRADE_COLORS[liveGrade]}30` }}>
              <GradeBadge grade={liveGrade} size="lg" />
              <div>
                <div className="text-sm font-bold" style={{ color: GRADE_COLORS[liveGrade] }}>{liveGrade} — {gradeStatus(liveGrade)}</div>
                <div className="text-xs" style={{ color: k.textMuted }}>{capturing ? 'Live from sensors' : 'ISO 20816 grade'}</div>
              </div>
            </div>
          )}

          {/* OCR notice */}
          {ocrNotice && (
            <div className="p-3 rounded-xl mb-4 text-sm font-semibold" style={{ backgroundColor: ocrNotice.includes('failed') || ocrNotice.includes('Could not') ? k.errBg : '#E8F6EE', color: ocrNotice.includes('failed') || ocrNotice.includes('Could not') ? k.errText : '#1E7A47', border: `1px solid ${ocrNotice.includes('failed') || ocrNotice.includes('Could not') ? k.errBorder : '#3DD68C'}` }}>
              {ocrNotice}
            </div>
          )}

          {/* Motor info form — disabled during capture */}
          <div className="space-y-3">
            {/* Scan Nameplate button */}
            {!capturing && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={scanning}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#059669', minHeight: 48 }}>
                {scanning ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Analyzing nameplate...</>
                ) : (
                  <><CameraIcon /> Scan Nameplate</>
                )}
              </button>
            )}

            <Field label="Asset Name / Description" required>
              <input ref={nameRef} value={currentMotor.name} onChange={e => setCurrentMotor(m => ({ ...m, name: e.target.value }))} placeholder="e.g. Cooling Tower Fan #1" autoFocus className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} disabled={capturing} />
            </Field>
            <Field label="Location">
              <input value={currentMotor.location} onChange={e => setCurrentMotor(m => ({ ...m, location: e.target.value }))} placeholder="e.g. Roof, Building A" className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} disabled={capturing} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="HP"><input type="number" inputMode="decimal" value={currentMotor.hp} onChange={e => setCurrentMotor(m => ({ ...m, hp: e.target.value }))} placeholder="50" className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} disabled={capturing} /></Field>
              <Field label="RPM"><input type="number" inputMode="decimal" value={currentMotor.rpm} onChange={e => setCurrentMotor(m => ({ ...m, rpm: e.target.value }))} placeholder="1750" className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} disabled={capturing} /></Field>
            </div>

            {/* Extra nameplate fields — show if OCR filled them */}
            {(currentMotor.manufacturer || currentMotor.frame || currentMotor.voltage || currentMotor.serialNumber) && !capturing && (
              <div className="grid grid-cols-2 gap-3">
                {currentMotor.manufacturer && <Field label="Manufacturer"><input value={currentMotor.manufacturer} onChange={e => setCurrentMotor(m => ({ ...m, manufacturer: e.target.value }))} className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>}
                {currentMotor.frame && <Field label="Frame"><input value={currentMotor.frame} onChange={e => setCurrentMotor(m => ({ ...m, frame: e.target.value }))} className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>}
                {currentMotor.voltage && <Field label="Voltage"><input value={currentMotor.voltage} onChange={e => setCurrentMotor(m => ({ ...m, voltage: e.target.value }))} className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>}
                {currentMotor.serialNumber && <Field label="Serial Number"><input value={currentMotor.serialNumber} onChange={e => setCurrentMotor(m => ({ ...m, serialNumber: e.target.value }))} className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>}
              </div>
            )}

            {/* Manual vibration/temp entry — only show when NOT capturing */}
            {!capturing && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Vibration (mm/s)"><input type="number" inputMode="decimal" step="0.01" value={currentMotor.vibration} onChange={e => setCurrentMotor(m => ({ ...m, vibration: e.target.value }))} placeholder="Manual entry" className={inputCls} style={{ ...inputStyle, borderColor: liveGrade ? `${GRADE_COLORS[liveGrade]}60` : k.border }} onFocus={focusTeal} onBlur={blurBorder} /></Field>
                  <Field label="Temp (°F)"><input type="number" inputMode="decimal" value={currentMotor.temperature} onChange={e => setCurrentMotor(m => ({ ...m, temperature: e.target.value }))} placeholder="Manual" className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} /></Field>
                </div>
              </>
            )}

            <Field label="Notes (optional)">
              <input value={currentMotor.notes} onChange={e => setCurrentMotor(m => ({ ...m, notes: e.target.value }))} placeholder="Loud noise from drive end" className={inputCls} style={inputStyle} onFocus={focusTeal} onBlur={blurBorder} disabled={capturing} />
            </Field>

            {/* ── Photo Attachments ─────────────────────────────────── */}
            {!capturing && (
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: k.textSec }}>Photos (up to 3)</label>
                {currentPhotos.length > 0 && (
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {currentPhotos.map((p, i) => (
                      <div key={i} className="relative" style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', border: `1px solid ${k.border}` }}>
                        {p.preview ? (
                          <img src={p.preview} alt={`Photo ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div className="flex items-center justify-center h-full" style={{ backgroundColor: k.card }}>
                            <ImageIcon />
                          </div>
                        )}
                        {p.uploading && (
                          <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          </div>
                        )}
                        <button onClick={() => removePhoto(i)} className="absolute top-0 right-0 w-5 h-5 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: '0 0 0 6px' }}>&times;</button>
                      </div>
                    ))}
                  </div>
                )}
                {currentPhotos.length < MAX_PHOTOS && (
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading}
                    className="w-full py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: k.card, border: `1px dashed ${k.border}`, color: k.textSec, minHeight: 44 }}>
                    {photoUploading ? (
                      <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> Uploading...</>
                    ) : (
                      <><ImageIcon /> Add Photos</>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* ── Thermal Analysis Result ──────────────────────────── */}
            {!capturing && (analyzingThermal || thermalAnalysis) && (
              <div className="p-3 rounded-xl text-sm" style={{
                backgroundColor: analyzingThermal ? `${TEAL}10` : '#f0fdfa',
                border: `1px solid ${analyzingThermal ? `${TEAL}30` : '#99f6e4'}`,
              }}>
                {analyzingThermal ? (
                  <div className="flex items-center gap-2" style={{ color: TEAL }}>
                    <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    <span className="font-semibold">Analyzing thermal image...</span>
                  </div>
                ) : (
                  <>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: TEAL }}>Thermal Analysis</div>
                    <div style={{ color: k.text, fontStyle: 'italic' }}>{thermalAnalysis}</div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Capture controls ─────────────────────────────────────── */}

          {capturing ? (
            <>
              {/* Live sensor panels */}
              <div className="mt-4">
                <div className="text-center mb-3">
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: '#E8F6EE', color: '#1E7A47', animation: 'pulse-dot 2s ease-in-out infinite' }}>
                    CAPTURING — {currentMotor.name}
                  </span>
                </div>
                <SensorPanel telemetry={telemetry} k={k} />

                {/* Status message */}
                <div className="text-center p-3 rounded-xl mb-4 text-sm font-semibold" style={{
                  backgroundColor: bothReady ? '#E8F6EE' : `${TEAL}15`,
                  color: bothReady ? '#1E7A47' : TEAL,
                  border: `1px solid ${bothReady ? '#3DD68C' : `${TEAL}30`}`,
                }}>
                  {bothReady
                    ? 'Both sensors ready — tap Next Asset when done'
                    : `Need ${MIN_READINGS}+ readings per sensor. Keep sensors mounted.`}
                </div>
              </div>

              {/* Next Motor button — gated on both sensors ready */}
              <button disabled={!bothReady} onClick={handleStopAndSave} className={btnCls} style={{ ...btnStyle, marginTop: 8 }}>
                {editingIdx !== null ? 'Update Asset' : 'Next Asset'}
              </button>
            </>
          ) : (
            <div className="space-y-3 mt-4">
              {/* Start Capture — primary action */}
              <button disabled={!canStartCapture} onClick={handleStartCapture}
                className={btnCls} style={{ ...btnStyle, backgroundColor: '#059669' }}>
                Start Capture
              </button>

              {/* OR divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px" style={{ backgroundColor: k.border }} />
                <span className="text-xs font-semibold" style={{ color: k.textMuted }}>or enter manually</span>
                <div className="flex-1 h-px" style={{ backgroundColor: k.border }} />
              </div>

              {/* Manual add — fallback */}
              <button disabled={!canAddMotor} onClick={addOrUpdateMotor}
                className={btnCls} style={{ ...btnStyle }}>
                {editingIdx !== null ? 'Update Asset' : 'Add Asset (Manual)'}
              </button>
            </div>
          )}

          {/* ── Captured motors list ────────────────────────────────── */}
          {motors.length > 0 && !capturing && <div className="mt-8">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: k.textMuted }}>Captured Assets ({motors.length})</h3>
            <div className="space-y-2">{motors.map((m, i) => (
              <div key={i}>
                {motorCard(m, i, 'capture')}
                {/* Quick Explanation panel — tech-only, not in PDF */}
                {(m.quickExplanation || (m.vibration > 0 && !m.quickExplanation)) && (() => {
                  const g = m.grade;
                  const panelColor = g === 'A' || g === 'B' ? '#1E7A47' : g === 'C' ? '#9A5B00' : g === 'D' ? '#C2410C' : '#C2410C';
                  const panelBg = g === 'A' || g === 'B' ? '#f0fdf4' : g === 'C' ? '#fefce8' : g === 'D' ? '#fff7ed' : '#fef2f2';
                  const panelDarkBg = g === 'A' || g === 'B' ? '#1a2e1a' : g === 'C' ? '#2e2a1a' : g === 'D' ? '#2e221a' : '#2e1a1a';
                  const panelEmoji = g === 'A' || g === 'B' ? '\u{1F7E2}' : g === 'C' ? '\u{1F7E1}' : g === 'D' ? '\u{1F7E0}' : '\u{1F534}';
                  return (
                    <div className="mt-1 p-3 rounded-xl text-sm" style={{
                      backgroundColor: theme.isDark ? panelDarkBg : panelBg,
                      border: `1px solid ${panelColor}30`,
                      borderLeft: `4px solid ${panelColor}`,
                    }}>
                      <div className="text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: panelColor, opacity: 0.8 }}>
                        Quick Explanation (for your reference)
                      </div>
                      {m.quickExplanation ? (
                        <div style={{ color: k.text, lineHeight: 1.5 }}>{panelEmoji} {m.quickExplanation}</div>
                      ) : explaining.has(i) ? (
                        <div className="flex items-center gap-2" style={{ color: k.textMuted }}>
                          <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                          Generating explanation...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2" style={{ color: k.textMuted }}>
                          Not generated yet.
                          <button onClick={() => fetchQuickExplanation(m, i)} className="font-semibold underline" style={{ color: k.textSec }}>Generate</button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}</div>
            {motors.length >= 3 && <button onClick={() => setStep(3)} className={btnCls} style={{ ...btnStyle, marginTop: 16 }}>Review &amp; Generate Report</button>}
          </div>}
        </>}

        {/* Step 3 — Review */}
        {step === 3 && (() => {
          const good = motors.filter(m => m.grade === 'A' || m.grade === 'B').length;
          const watch = motors.filter(m => m.grade === 'C').length;
          const attention = motors.filter(m => m.grade === 'D').length;
          const critical = motors.filter(m => m.grade === 'F').length;
          return <>
            <StepHeader num={3} title="Review" subtitle={`${motors.length} assets · ${facility.company}`} />
            <div className="flex flex-wrap gap-2 mb-6">
              {good > 0 && <Badge color="#1E7A47" label={`${good} Good`} />}
              {watch > 0 && <Badge color="#9A5B00" label={`${watch} Watch`} />}
              {attention > 0 && <Badge color="#C2410C" label={`${attention} Attention`} />}
              {critical > 0 && <Badge color="#C2410C" label={`${critical} Critical`} />}
            </div>
            <div className="space-y-2 mb-6">{motors.map((m, i) => motorCard(m, i, 'review'))}</div>
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="flex-1 py-4 rounded-xl text-base font-semibold transition" style={{ backgroundColor: k.backBtnBg, border: `1px solid ${k.border}`, minHeight: 52, color: k.backBtnText }}>Back</button>
              <button onClick={handleGenerate} disabled={motors.length < 3} className="flex-1 py-4 rounded-xl text-base font-bold text-white transition disabled:opacity-40" style={{ ...btnStyle, minHeight: 52 }}>Generate Report</button>
            </div>
          </>;
        })()}

        {/* Step 4 — Report */}
        {step === 4 && <>
          <StepHeader num={4} title={generating ? 'Generating Report...' : 'Report Ready'} subtitle={facility.company} />
          {generating && <div className="text-center py-16">
            <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: `${TEAL}30`, borderTopColor: TEAL }} />
            <p className="text-sm" style={{ color: k.textSec }}>Generating your screening report...</p>
          </div>}
          {reportError && <div className="p-4 rounded-xl mb-4 text-sm" style={{ backgroundColor: k.errBg, border: `1px solid ${k.errBorder}`, color: k.errText }}>{reportError}</div>}
          {reportUrl && !generating && <>
            <div className="text-center py-8 rounded-xl mb-6" style={{ backgroundColor: k.card, border: `1px solid ${k.border}`, transition: 'background-color 0.2s' }}>
              <div className="text-4xl mb-3" style={{ color: TEAL }}>&#10003;</div>
              <h3 className="text-xl font-bold mb-2">Report ready</h3>
              <p className="text-sm mb-6" style={{ color: k.textSec }}>{motors.length} assets · {facility.company}</p>
              <a href={reportUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-base font-bold text-white transition" style={{ backgroundColor: TEAL, minHeight: 48 }}>Download PDF Report</a>
            </div>
            <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: k.card, border: `1px solid ${k.border}`, transition: 'background-color 0.2s' }}>
              <h3 className="text-sm font-bold mb-3">Email Report to Customer</h3>
              <div className="flex gap-2">
                <input type="email" value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="customer@company.com" className={inputCls} style={{ ...inputStyle, flex: 1 }} onFocus={focusTeal} onBlur={blurBorder} />
                <button disabled={!sendEmail || sendStatus === 'sending'} onClick={handleSendEmail} className="px-5 py-3.5 rounded-xl font-bold text-white text-sm transition disabled:opacity-40" style={{ backgroundColor: TEAL, minHeight: 48, whiteSpace: 'nowrap' }}>{sendStatus === 'sending' ? 'Sending...' : sendStatus === 'sent' ? 'Sent!' : 'Send Report'}</button>
              </div>
              {sendStatus === 'sent' && <p className="text-xs mt-2" style={{ color: TEAL }}>Report link sent to {sendEmail}</p>}
            </div>
            <button onClick={handleReset} className={btnCls} style={{ ...btnStyle, backgroundColor: k.backBtnBg, border: `1px solid ${k.border}`, color: k.backBtnText }}>Start New Screening</button>
          </>}
        </>}
      </Shell>
    </ThemeCtx.Provider>
  );
}
